// Red/Yellow/Green affordability scoring system

export function calculateHouseScore(house, householdProfile) {
  if (!householdProfile || !house.analysis_data) {
    return {
      score: 'unknown',
      reason: 'Insufficient data for scoring',
      details: {}
    };
  }

  const monthlyIncome = householdProfile.annual_income / 12;
  const monthlyHousing = house.analysis_data.totalMonthly;
  const housingRatio = monthlyHousing / monthlyIncome;
  
  const totalMonthlyDebt = (householdProfile.monthly_debt_payments || 0) + monthlyHousing;
  const dti = totalMonthlyDebt / monthlyIncome;
  
  const cashNeeded = house.analysis_data.totalCashNeeded;
  const currentSavings = householdProfile.current_savings || 0;
  const hasEnough = currentSavings >= cashNeeded;
  const emergencyFund = monthlyHousing * 3; // 3 months of housing payments
  const hasEmergency = currentSavings >= (cashNeeded + emergencyFund);
  
  // RED conditions (Can't Afford)
  if (housingRatio > 0.28 || dti > 0.43 || !hasEnough) {
    return {
      score: 'red',
      reason: 'Above recommended affordability limits',
      details: {
        housingRatio: (housingRatio * 100).toFixed(1) + '%',
        dti: (dti * 100).toFixed(1) + '%',
        cashShortfall: hasEnough ? 0 : cashNeeded - currentSavings,
        monthlyIncome: monthlyIncome.toFixed(0),
        monthlyHousing: monthlyHousing.toFixed(0),
        totalMonthlyDebt: totalMonthlyDebt.toFixed(0),
        recommendations: [
          housingRatio > 0.28 ? 'Housing payment exceeds 28% of income' : null,
          dti > 0.43 ? 'Total debt-to-income exceeds 43%' : null,
          !hasEnough ? `Need $${(cashNeeded - currentSavings).toLocaleString()} more for down payment and closing` : null
        ].filter(Boolean)
      }
    };
  }
  
  // YELLOW conditions (Risky but Manageable)
  if (housingRatio > 0.25 || dti > 0.36 || !hasEmergency) {
    return {
      score: 'yellow',
      reason: 'Tight but potentially manageable',
      details: {
        housingRatio: (housingRatio * 100).toFixed(1) + '%',
        dti: (dti * 100).toFixed(1) + '%',
        emergencyFundShort: hasEmergency ? 0 : (cashNeeded + emergencyFund) - currentSavings,
        monthlyIncome: monthlyIncome.toFixed(0),
        monthlyHousing: monthlyHousing.toFixed(0),
        totalMonthlyDebt: totalMonthlyDebt.toFixed(0),
        recommendations: [
          housingRatio > 0.25 ? 'Housing payment is 25-28% of income (getting tight)' : null,
          dti > 0.36 ? 'Total debt-to-income is 36-43% (higher risk)' : null,
          !hasEmergency ? `Consider saving $${((cashNeeded + emergencyFund) - currentSavings).toLocaleString()} more for emergency fund` : null
        ].filter(Boolean)
      }
    };
  }
  
  // GREEN (Good Zone)
  return {
    score: 'green',
    reason: 'Well within recommended limits',
    details: {
      housingRatio: (housingRatio * 100).toFixed(1) + '%',
      dti: (dti * 100).toFixed(1) + '%',
      cushion: currentSavings - (cashNeeded + emergencyFund),
      monthlyIncome: monthlyIncome.toFixed(0),
      monthlyHousing: monthlyHousing.toFixed(0),
      totalMonthlyDebt: totalMonthlyDebt.toFixed(0),
      recommendations: [
        'Housing payment is under 25% of income ✓',
        'Total debt-to-income is under 36% ✓',
        'Have enough saved for down payment, closing, and emergency fund ✓'
      ]
    }
  };
}

export function getScoreColor(score) {
  switch (score) {
    case 'red':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'yellow':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'green':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

export function getScoreIcon(score) {
  switch (score) {
    case 'red':
      return '🚫';
    case 'yellow':
      return '⚠️';
    case 'green':
      return '✅';
    default:
      return '❓';
  }
}